'use client'

import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType
} from 'embla-carousel-react'
import { cn } from '@/modules/lib/utils'
import { Shevron } from '~shared/icons/shevron'
import { ArrowButton } from './arrow-button'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

// const DisableDragProvider = ({children}: {children: React.ReactNode}) => {

//   const embla = useCarousel();

// useEffect(() => {
//   embla?.on('pointerDown', (event) => {
//     // Дополнительно, если нужно вручную исключить drag
//     if ((event.target as HTMLElement).closest('.radix-slider')) {
//       event.stopPropagation();
//     }
//   });
// }, [embla]);
//   return children;
// }

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isDraggableRef = React.useRef(true)
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        watchDrag: () => isDraggableRef.current,
        axis: orientation === 'horizontal' ? 'x' : 'y'
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    // ignore drag inside .embla-drag-ignore
    React.useEffect(() => {
      if (!api) return

      const reenable = () => {
        isDraggableRef.current = true

        window.removeEventListener('pointerup', reenable)
        window.removeEventListener('touchend', reenable)
      }

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as HTMLElement

        if (
          api.rootNode().contains(target) &&
          target.closest('.embla-drag-ignore')
        ) {
          isDraggableRef.current = false

          window.addEventListener('pointerup', reenable)
          window.addEventListener('touchend', reenable)
        }
      }

      document.addEventListener('pointerdown', handlePointerDown, true)
      return () => {
        window.removeEventListener('pointerup', reenable)
        window.removeEventListener('touchend', reenable)
        document.removeEventListener('pointerdown', handlePointerDown, true)
      }
    }, [api])

    const contextValue = React.useMemo(
      () => ({
        carouselRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      }),
      [
        carouselRef,
        api,
        opts,
        orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      ]
    )

    return (
      <CarouselContext.Provider value={contextValue}>
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}>
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className='overflow-hidden h-full'>
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  { className?: string; size: 'lg' | 'sm' }
>(({ className, size, ...props }, ref) => {
  const { scrollPrev, canScrollPrev, canScrollNext } = useCarousel()

  return canScrollNext || canScrollPrev ? (
    <ArrowButton
      onClick={scrollPrev}
      ref={ref}
      className={className}
      disabled={!canScrollPrev}
      size={size}
      direction='left'
      {...props}
    />
  ) : null
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  { className?: string; size: 'lg' | 'sm' }
>(({ className, size, ...props }, ref) => {
  const { scrollNext, canScrollNext, canScrollPrev } = useCarousel()

  return canScrollPrev || canScrollNext ? (
    <ArrowButton
      onClick={scrollNext}
      ref={ref}
      className={className}
      disabled={!canScrollNext}
      size={size}
      direction='right'
      {...props}
    />
  ) : null
})
CarouselNext.displayName = 'CarouselNext'

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext
}
